/**
 * Опции для получения top offset элемента
 */
export interface GetElementTopOffsetOptions {
  /**
   * Учитывать отступ сверху (например, высоту sticky header)
   * @default 0
   */
  offset?: number;

  /**
   * Возвращать абсолютное значение (без учета знака)
   * @default false
   */
  absolute?: boolean;
}

/**
 * Получает расстояние от верхней границы окна до верхней границы элемента
 *
 * @param selector - CSS селектор элемента
 * @param options - Опции получения offset
 * @returns Расстояние в пикселях или null если элемент не найден
 *
 * Положительное значение - элемент ниже верхней границы окна
 * Отрицательное значение - элемент выше верхней границы окна (скроллен вверх)
 * 0 - элемент точно на верхней границе окна
 *
 * @example
 * // Базовое использование
 * const top = getElementTopOffset('.my-element')
 * // top = 150 (элемент на 150px ниже верхней границы окна)
 *
 * @example
 * // С учетом высоты header
 * const top = getElementTopOffset('.my-element', { offset: 62 })
 * // top = 88 (150 - 62 = 88px от нижней границы header)
 *
 * @example
 * // Абсолютное значение
 * const top = getElementTopOffset('.my-element', { absolute: true })
 * // top = 150 (всегда положительное)
 */
export function getElementTopOffset(
  selector: string,
  options: GetElementTopOffsetOptions = {}
): number | null {
  const element = document.querySelector(selector);
  if (!element) return null;

  const { offset = 0, absolute = false } = options;

  const rect = element.getBoundingClientRect();
  const topOffset = rect.top - offset;

  return absolute ? Math.abs(topOffset) : topOffset;
}

/**
 * Получает расстояние от верхней границы окна до нижней границы элемента
 *
 * @param selector - CSS селектор элемента
 * @param options - Опции получения offset
 * @returns Расстояние в пикселях или null если элемент не найден
 *
 * @example
 * const bottom = getElementBottomOffset('.my-element')
 * // bottom = 250 (нижняя граница элемента на 250px ниже верхней границы окна)
 */
export function getElementBottomOffset(
  selector: string,
  options: GetElementTopOffsetOptions = {}
): number | null {
  const element = document.querySelector(selector);
  if (!element) return null;

  const { offset = 0, absolute = false } = options;

  const rect = element.getBoundingClientRect();
  const bottomOffset = rect.bottom - offset;

  return absolute ? Math.abs(bottomOffset) : bottomOffset;
}

/**
 * Проверяет, находится ли элемент выше верхней границы окна (скроллен вверх)
 *
 * @param selector - CSS селектор элемента
 * @param options - Опции проверки
 * @returns true если элемент выше верхней границы
 *
 * @example
 * const isAbove = isElementAboveViewport('.my-element')
 * // isAbove = true (элемент скроллен вверх за пределы экрана)
 */
export function isElementAboveViewport(
  selector: string,
  options: GetElementTopOffsetOptions = {}
): boolean {
  const topOffset = getElementTopOffset(selector, options);
  return topOffset !== null && topOffset < 0;
}

/**
 * Проверяет, находится ли элемент ниже нижней границы окна
 *
 * @param selector - CSS селектор элемента
 * @param options - Опции проверки
 * @returns true если элемент ниже нижней границы
 *
 * @example
 * const isBelow = isElementBelowViewport('.my-element')
 * // isBelow = true (элемент находится ниже видимой области)
 */
export function isElementBelowViewport(
  selector: string,
  options: GetElementTopOffsetOptions = {}
): boolean {
  const element = document.querySelector(selector);
  if (!element) return false;

  const { offset = 0 } = options;
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight;

  return rect.top - offset > viewportHeight;
}

/**
 * Получает информацию о позиции элемента относительно viewport
 *
 * @param selector - CSS селектор элемента
 * @param options - Опции получения информации
 * @returns Объект с информацией о позиции или null если элемент не найден
 *
 * @example
 * const position = getElementViewportPosition('.my-element', { offset: 62 })
 * // {
 * //   top: 150,
 * //   bottom: 250,
 * //   isAbove: false,
 * //   isBelow: false,
 * //   isVisible: true,
 * //   distanceFromTop: 150,
 * //   distanceFromBottom: 750
 * // }
 */
export function getElementViewportPosition(
  selector: string,
  options: GetElementTopOffsetOptions = {}
) {
  const element = document.querySelector(selector);
  if (!element) return null;

  const { offset = 0 } = options;
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight;

  const top = rect.top - offset;
  const bottom = rect.bottom - offset;

  return {
    top,
    bottom,
    isAbove: top < 0,
    isBelow: top > viewportHeight,
    isVisible: bottom > 0 && top < viewportHeight,
    distanceFromTop: top,
    distanceFromBottom: viewportHeight - bottom,
    height: rect.height,
    viewportHeight,
  };
}
